import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { NumberFormatter } from '@repo/toolkit/number'
import { StringFormatter } from '@repo/toolkit/string'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import Button from '@repo/ui/button'
import Checkbox from '@repo/ui/checkbox'
import DataTable, { Column, Row } from '@repo/ui/data-table'
import {
  BellIcon,
  ChevronLeftIcon,
  EyeOpenIcon,
  GoalIcon,
  SendIcon,
  UserFourIcon
} from '@repo/ui/icons'
import Rating from '@repo/ui/rating'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { API } from '../../api/apis.config'
import { markMissionCompleted } from '../../api/mission-to-staffs/mark-mission-completed'
import { markMissionIncomplete } from '../../api/mission-to-staffs/mark-mission-incomplete'
import { getMissionTracking } from '../../api/mission-trackings/get-mission-tracking'
import { getMissionTrackingAffectedStaffs } from '../../api/mission-trackings/get-mission-tracking-affected-staffs'
import { sendMissionTrackingContracts } from '../../api/mission-trackings/send-contracts'
import { sendMissionFriendlyContractReminderToStaffs } from '../../api/mission-trackings/send-email'
import {
  updateMissionTracking as updateMissionTrackingAPI,
  updateMissionTrackingExceptionalBonus as updateMissionTrackingExceptionalBonusAPI
} from '../../api/mission-trackings/update-mission-tracking'
import { updateMissionTrackingMissionToStaff } from '../../api/mission-trackings/update-mission-tracking-mission-to-staff'
import { DPAESection } from '../../components/elements/dpae-section'
import { ItemNotFound } from '../../components/elements/item-not-found'
import LoadingScreen from '../../components/elements/loading/loading-screen'
import UserNavBar from '../../components/elements/nav-bar'
import { QualificationTags } from '../../components/elements/qualification-badge'
import MissionToStaffReportBadge from '../../components/pages/mission-trackings/mission-to-staff-report-badge'
import { MTBadge } from '../../components/pages/mission-trackings/mission-tracking.ui'
import MissionTrackingExceptionalBonusForm from '../../components/pages/mission-trackings/mission-tracking-exceptional-bonus-form'
import { MissionTrackingForm } from '../../components/pages/mission-trackings/mission-tracking-form'
import { mapMissionTrackingToInput } from '../../components/pages/mission-trackings/utils'
import { Paths } from '../../lib/constants/path'
import { CurrencyEnum } from '../../lib/types/enums/common/currency.enum'
import { missionStatusEnumMapping } from '../../lib/types/enums/mission/mission-status.enum'
import { tripStepTypeMapping } from '../../lib/types/enums/trip/trip-type.enum'
import { Mission } from '../../lib/types/models/mission/mission'
import { MissionProperties } from '../../lib/types/models/mission/properties.mission'
import { MissionToStaff } from '../../lib/types/models/mission-to-staff/mission-to-staff'
import { MissionTrackingInput } from '../../lib/types/models/mission-tracking/mission-tracking-input'
import { StaffProperties } from '../../lib/types/models/staff/properties.staff'
import { Time } from '../../lib/types/utils'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'
import NotFoundPage from '../common/not-found'

export function MissionTrackingUpdatePage() {
  const { id: missionTrackingId } = useParams()
  const queryClient = useQueryClient()
  const { navigate, back, navigateWithEvent } = useNavigator()

  if (!missionTrackingId || isNaN(parseInt(missionTrackingId)))
    return <ItemNotFound title={'Identifiant de barème invalide'} />

  const missionTracking = useQuery({
    queryKey: ['mission-trackings', missionTrackingId],
    queryFn: () => getMissionTracking(parseInt(missionTrackingId)),
    enabled: !!missionTrackingId
  })
  const affectedStaffs = useQuery({
    queryKey: ['mission-tracking-affected-staffs', missionTrackingId],
    queryFn: () => getMissionTrackingAffectedStaffs(parseInt(missionTrackingId)),
    enabled: !!missionTrackingId
  })

  const updateMissionTracking = useMutation({
    mutationFn: updateMissionTrackingAPI,
    onError: showAxiosError,
    onSuccess: () => toast.success('Mission tracking mise à jour avec succès')
  })

  const updateMissionTrackingExceptionalBonus = useMutation({
    mutationFn: updateMissionTrackingExceptionalBonusAPI,
    onError: showAxiosError,
    onSuccess: () => toast.success('Mise à jour de la prime exceptionnelle réussie')
  })

  const sendContracts = useMutation({
    mutationFn: sendMissionTrackingContracts,
    onSuccess: () => toast.success('Les contrats ont été envoyés avec succès'),
    onError: showAxiosError
  })

  const sendMissionFriendlyContractReminderToStaffsMutation = useMutation({
    mutationFn: sendMissionFriendlyContractReminderToStaffs,
    onError: showAxiosError,
    onSuccess: () => toast.success('Les rappels amicaux ont été envoyés avec succès')
  })

  const updateMissionToStaff = useMutation({
    mutationFn: updateMissionTrackingMissionToStaff,
    onSuccess: () => {
      affectedStaffs.refetch()
      toast.success('La note a été mise à jour avec succès')
    },
    onError: showAxiosError
  })

  const markMissionCompletedMutation = useMutation({
    mutationFn: markMissionCompleted,
    onSuccess: () => {
      affectedStaffs.refetch()
      queryClient.invalidateQueries({
        queryKey: ['mission-tracking-affected-staffs', missionTrackingId]
      })
      toast.success('Le staff a été marqué comme ayant accompli la mission')
    },
    onError: showAxiosError
  })

  const markMissionIncompleteMutation = useMutation({
    mutationFn: markMissionIncomplete,
    onSuccess: () => {
      affectedStaffs.refetch()
      queryClient.invalidateQueries({
        queryKey: ['mission-tracking-affected-staffs', missionTrackingId]
      })
      toast.success("Le staff a été marqué comme n'ayant pas accompli la mission")
    },
    onError: showAxiosError
  })

  async function handleFormSubmit(data: MissionTrackingInput) {
    if (!missionTrackingId) return

    await updateMissionTracking.mutateAsync({ id: parseInt(missionTrackingId), ...data })
    missionTracking.refetch()
    affectedStaffs.refetch()
  }

  async function handleSendContract() {
    if (!missionTrackingId) return

    await sendContracts.mutateAsync({ ids: [parseInt(missionTrackingId)] })
    missionTracking.refetch()
    affectedStaffs.refetch()
  }

  function getSortingFn(
    date: keyof Mission,
    rowA: Row<MissionToStaff>,
    rowB: Row<MissionToStaff>
  ) {
    const missionA = rowA.original.mission
    const missionB = rowB.original.mission

    const dateA = missionA?.[date] ? DateFormatter.createDate(missionA?.[date] as Time) : null
    const dateB = missionB?.[date] ? DateFormatter.createDate(missionB?.[date] as Time) : null

    if (!dateA && !dateB) return 0
    if (!dateA) return 1 // Push null dates to end
    if (!dateB) return 1 // Push null dates to end
    return dateA.getTime() - dateB.getTime()
  }

  const missionToStaffColumns: Column<MissionToStaff>[] = [
    {
      accessorKey: 'id',
      header: '#',
      enableSorting: true
    },
    {
      accessorKey: 'mission.directionality',
      header: 'Type de trajet',
      enableSorting: true,
      cell: ({ row }) => {
        const details = tripStepTypeMapping[row.original.mission.directionality]
        return <Badge text={details.label} variant={details.variant} />
      }
    },
    {
      accessorKey: 'mission.date',
      header: 'Date',
      enableSorting: true,
      cell: ({ row }) => <Badge text={DateFormatter.DDMMYYYY(row.original.mission.date)} />
    },
    {
      accessorKey: 'mission.title',
      header: 'Libellé',
      enableSorting: true
    },
    {
      accessorKey: 'budget',
      header: 'Rémunération',
      cell: ({ row }) => (
        <Badge
          size={'xsmall'}
          variant={'darkBlue'}
          text={NumberFormatter.formatCurrency(row.original.budget ?? 0, CurrencyEnum.EUR)}
        />
      )
    },
    {
      accessorKey: 'mission.appointmentTime',
      header: 'Heure de RDV Mission',
      enableSorting: true,
      sortingFn: (rowA, rowB) => getSortingFn('appointmentTime', rowA, rowB),
      cell: ({ row }) => {
        const appointmentTime = row.original.mission.appointmentTime
        const text = appointmentTime
          ? DateFormatter.HHmm(DateFormatter.createDate(appointmentTime))
          : '-'
        return <Badge text={text} />
      }
    },
    {
      accessorKey: 'mission.departureTime',
      header: 'Heures de départ',
      enableSorting: true,
      sortingFn: (rowA, rowB) => getSortingFn('departureTime', rowA, rowB),
      cell: ({ row }) => {
        return <Badge text={MissionProperties.displayDepartureTimes(row.original.mission)} />
      }
    },
    {
      accessorKey: 'mission.arrivalTime',
      header: "Heures d'arrivée",
      enableSorting: true,
      sortingFn: (rowA, rowB) => getSortingFn('arrivalTime', rowA, rowB),
      cell: ({ row }) => {
        return <Badge text={MissionProperties.displayArrivalTimes(row.original.mission)} />
      }
    },
    {
      accessorKey: 'mission.meetingPlace.label',
      header: 'Lieu de RDV',
      enableSorting: true
    },
    {
      accessorKey: 'mission.endPlace.label',
      header: 'Lieu de fin de mission',
      enableSorting: true,
      cell: ({ row }) => valueRender(row.original.mission.endPlace?.label)
    },
    {
      accessorKey: 'mission.endPlace.city',
      header: 'A destination de',
      enableSorting: true,
      cell: ({ row }) => valueRender(row.original.mission.endPlace?.city)
    },
    {
      accessorKey: 'mission.meetingPlace.city',
      header: 'En provenance de',
      enableSorting: true,
      cell: ({ row }) => valueRender(row.original.mission.meetingPlace?.city)
    },
    {
      accessorKey: 'mission.qualifications',
      header: 'Qualifications requises',
      cell: ({ row }) => {
        return <QualificationTags qualifications={row.original.mission.qualifications} />
      }
    },
    {
      accessorKey: 'mission.staffCount',
      header: 'Effectifs mission'
    },
    {
      accessorKey: 'mission.status',
      header: 'Statut',
      enableSorting: true,
      cell: ({ row }) => {
        const status = row.original.mission.status
        const details = missionStatusEnumMapping[status]
        return <Badge text={details.label} variant={details.variant} />
      }
    },

    {
      accessorKey: 'contract.contractSentAt',
      header: "Date d'envoi",
      enableSorting: true,
      cell: ({ row }) => {
        const contractSentAt = row.original.contract?.contractSentAt
        const text = contractSentAt ? DateFormatter.DDMMYYYY(contractSentAt) : '-'
        return <Badge text={text} />
      }
    },
    {
      accessorKey: 'contract.contractSignedAt',
      header: 'Date de signature',
      enableSorting: true,
      cell: ({ row }) => {
        const contractSignedAt = row.original.contract?.contractSignedAt
        const text = contractSignedAt ? DateFormatter.DDMMYYYY(contractSignedAt) : '-'
        return <Badge text={text} />
      }
    },
    {
      accessorKey: 'contract.contractSignedAt',
      header: 'Contrat',
      cell: ({ row }) => {
        const documentFile = row.original.contract?.documentFile

        if (!documentFile) return '-'
        return (
          <Typography.Link
            variant={'label-medium'}
            onClick={(e) => {
              e.stopPropagation()
              if (documentFile) window.open(documentFile.path, '_blank')
            }}
          >
            Voir contrat
          </Typography.Link>
        )
      }
    },
    {
      accessorKey: 'rating',
      header: 'Évaluation',
      enableSorting: true,
      cell: ({ row }) => (
        <Rating
          mode={'edit'}
          defaultValue={row.original.rating}
          onChange={(value) =>
            updateMissionToStaff.mutate({
              id: parseInt(missionTrackingId),
              missionToStaffId: row.original.id,
              rating: value
            })
          }
        />
      )
    },
    {
      accessorKey: 'reportStatus',
      header: 'Rapport',
      cell: ({ row }) => (
        <MissionToStaffReportBadge
          missionToStaffId={row.original.id}
          reportStatus={row.original.reportStatus}
        />
      )
    },
    {
      accessorKey: 'missionCompleted',
      header: 'Mission effectuée',
      cell: ({ row }) => {
        return (
          <Checkbox
            checked={row.original.missionCompleted}
            disabled={
              markMissionCompletedMutation.isPending || markMissionIncompleteMutation.isPending
            }
            onChange={(checked) => {
              if (checked) markMissionCompletedMutation.mutate(row.original.id)
              else markMissionIncompleteMutation.mutate(row.original.id)
            }}
          />
        )
      }
    },
    {
      accessorKey: 'id',
      header: '',
      cell: ({ row }) => {
        const path = Paths.MISSION_VIEW_PAGE.replace(':id', row.original.mission.id.toString())

        return (
          <div
            className="flex items-center justify-center m-2 w-6 h-6 min-h-6 min-w-6 rounded-full bg-primary-light cursor-pointer"
            onClick={(e) => {
              e.stopPropagation()
              navigate(path)
            }}
          >
            <EyeOpenIcon fill={tailwindColors.primary.DEFAULT} className={'w-5 h-5'} />
          </div>
        )
      }
    }
  ]

  if (missionTracking.isLoading) return <LoadingScreen />
  if (!missionTracking.data) return <NotFoundPage />

  const { staff } = missionTracking.data
  const { contract, canSendContract } = missionTracking.data
  const totalPay = affectedStaffs.data?.reduce((acc, curr) => acc + (curr.pay ?? 0), 0)

  return (
    <div>
      <UserNavBar title={'Suivi des missions'} icon={GoalIcon} />
      <div className="h-4" />
      <Box className={'flex flex-col gap-4'}>
        <Button
          icon={ChevronLeftIcon}
          label="Retour"
          variant="secondary"
          className="flex justify-start w-fit"
          onClick={() => back(Paths.MISSION_TRACKING_LIST_PAGE)}
        />

        <div className="flex gap-4 w-full items-center border p-4 rounded-xl border-grey-light-hover">
          <div className="rounded-2xl w-24 h-24 object-cover bg-grey-light-hover flex justify-center items-center">
            {staff?.picture ? (
              <img
                src={staff?.picture.path}
                alt={'profile'}
                className={'w-full h-full object-cover rounded-2xl cursor-pointer'}
              />
            ) : (
              <UserFourIcon
                filled
                className={'w-full h-full'}
                color={tailwindColors.white.DEFAULT}
              />
            )}
          </div>

          <div className="flex gap-1 flex-col items-start">
            <Typography.Link
              variant={'body-medium'}
              onClick={(event) => {
                navigateWithEvent(
                  Paths.STAFF_UPDATE_PAGE.replace(':id', staff.id.toString()),
                  event
                )
              }}
            >
              {`${staff.lastName.toUpperCase()} ${StringFormatter.capitalize(staff.firstName)}`}
            </Typography.Link>

            <Typography variant={'secondary-body'}>{staff.address1}</Typography>

            <Typography variant={'secondary-body'}>
              {`Ancienneté : ${StaffProperties.getSeniority(staff)}`}
            </Typography>

            <div className={'flex flex-row gap-2 items-center'}>
              <Typography variant={'secondary-body'}>
                {`${staff.phoneNumber} - Email :`}
              </Typography>

              <Typography.Link variant={'secondary-body'}>
                <a href={`mailto:${staff.user.email}`}>{staff.user.email}</a>
              </Typography.Link>
            </div>
          </div>
        </div>

        <div className={'border p-4 rounded-xl border-grey-light-hover flex flex-col gap-5'}>
          <div className={'flex flex-row gap-3 justify-between'}>
            <div className={'flex flex-row justify-between items-center gap-3'}>
              <Typography variant={'body-medium'}>Infos contrat et avenant</Typography>

              {/* only show if contract is not signed */}
              {!contract?.contractSignedAt && contract?.contractSentAt && (
                <Button
                  label={'Envoyer une relance'}
                  icon={BellIcon}
                  variant={'ghost'}
                  onClick={async () =>
                    await sendMissionFriendlyContractReminderToStaffsMutation.mutateAsync({
                      ids: [parseInt(missionTrackingId)]
                    })
                  }
                  loading={sendMissionFriendlyContractReminderToStaffsMutation.isPending}
                />
              )}
            </div>

            <div className={'flex flex-row gap-3 justify-between items-center'}>
              <Button
                label={'Envoyer le contrat'}
                icon={SendIcon}
                onClick={handleSendContract}
                loading={sendContracts.isPending}
                disabled={!canSendContract}
              />
            </div>
          </div>

          <div className={'flex flex-row gap-16'}>
            <MTBadge
              type="date"
              label="Date d'envoi du contrat"
              value={contract?.contractSentAt}
            />
            <MTBadge label="Date de signature" type="date" value={contract?.contractSignedAt} />

            <div className={'flex flex-col gap-2 items-start'}>
              <Typography variant={'label-medium'} color={tailwindColors.grey.DEFAULT}>
                Rémunération totale
              </Typography>

              {!totalPay ? (
                '-'
              ) : (
                <Badge size={'xsmall'} text={`${totalPay?.toFixed(2)}€`} variant="darkBlue" />
              )}
            </div>
          </div>

          <MissionTrackingExceptionalBonusForm
            defaultValues={{
              exceptionalBonusAmount: missionTracking.data.exceptionalBonusAmount,
              exceptionalBonusReason: missionTracking.data.exceptionalBonusReason
            }}
            isLoading={updateMissionTrackingExceptionalBonus.isPending}
            onSubmit={async (data) => {
              if (!missionTrackingId) return
              await updateMissionTrackingExceptionalBonus.mutateAsync({
                id: parseInt(missionTrackingId),
                ...data
              })
              missionTracking.refetch()
              affectedStaffs.refetch()
            }}
          />

          <MissionTrackingForm
            defaultValues={mapMissionTrackingToInput(missionTracking.data)}
            onSubmit={handleFormSubmit}
            isLoading={updateMissionTracking.isPending}
          />
        </div>

        <DPAESection
          id={missionTracking.data.id}
          endpoint={API.missionTrackings.exportDpaeXml.url}
          dpaeDownloadAt={missionTracking.data.dpaeDownloadAt}
          dpaeNumber={missionTracking.data.dpaeNumber}
          dpaeFilledAt={missionTracking.data.dpaeFilledAt}
          onDownloadDpaeXml={() => missionTracking.refetch()}
        />

        <div className={'border p-4 rounded-xl border-grey-light-hover flex flex-col gap-5'}>
          <div className={'flex flex-row gap-2 items-center'}>
            <Typography variant={'body-medium'} color={tailwindColors.primary.DEFAULT}>
              Liste des missions
            </Typography>
          </div>

          <DataTable<MissionToStaff>
            columns={missionToStaffColumns}
            data={affectedStaffs.data ?? []}
            manualSorting={false}
            rowCount={affectedStaffs.data?.length ?? 0}
            paginated={false}
            disableHover
            displayEmptyState={false}
          />
        </div>
      </Box>
    </div>
  )
}
